import { HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { APIResponseWrapper } from '../../models/common/api-response-wrapper';
import * as M from './hr-admin-operations.models';
import { Constructor, HrApiClient } from '../shared/hr-api-client';

export interface AdminOperationsScopeParams {
  owningCompanyId?: number;
  companyGroupId?: number;
  memberCompanyId?: number;
}

export function withHrAdminOperationsApi<TBase extends Constructor<HrApiClient>>(Base: TBase) {
  return class extends Base {
    private readonly adminOperationsUrl = `${this.baseUrl}/admin-operations`;

    adminOperationsDashboard(scope: AdminOperationsScopeParams = {}): Observable<APIResponseWrapper<M.AdminOperationsDashboard>> {
      return this.http.get<APIResponseWrapper<M.AdminOperationsDashboard>>(`${this.adminOperationsUrl}/dashboard`, { params: this.params(scope) });
    }

    adminOperationsScopes(scope: AdminOperationsScopeParams = {}): Observable<APIResponseWrapper<M.AdminOperationsScope[]>> {
      return this.http.get<APIResponseWrapper<M.AdminOperationsScope[]>>(`${this.adminOperationsUrl}/scopes`, { params: this.params(scope) });
    }

    saveAdminOperationsScope(request: M.AdminOperationsScope): Observable<APIResponseWrapper<M.AdminOperationsScope>> {
      return request.scopeId
        ? this.http.put<APIResponseWrapper<M.AdminOperationsScope>>(`${this.adminOperationsUrl}/scopes/${request.scopeId}`, request)
        : this.http.post<APIResponseWrapper<M.AdminOperationsScope>>(`${this.adminOperationsUrl}/scopes`, request);
    }

    adminOperationsAuthorities(scope: AdminOperationsScopeParams = {}): Observable<APIResponseWrapper<M.AdminOperationsAuthority[]>> {
      return this.http.get<APIResponseWrapper<M.AdminOperationsAuthority[]>>(`${this.adminOperationsUrl}/authorities`, { params: this.params(scope) });
    }

    saveAdminOperationsAuthority(request: M.AdminOperationsAuthority): Observable<APIResponseWrapper<M.AdminOperationsAuthority>> {
      return request.authorityId
        ? this.http.put<APIResponseWrapper<M.AdminOperationsAuthority>>(`${this.adminOperationsUrl}/authorities/${request.authorityId}`, request)
        : this.http.post<APIResponseWrapper<M.AdminOperationsAuthority>>(`${this.adminOperationsUrl}/authorities`, request);
    }

    adminOperationsObligations(scope: AdminOperationsScopeParams = {}): Observable<APIResponseWrapper<M.AdminOperationsObligation[]>> {
      return this.http.get<APIResponseWrapper<M.AdminOperationsObligation[]>>(`${this.adminOperationsUrl}/obligations`, { params: this.params(scope) });
    }

    saveAdminOperationsObligation(request: M.AdminOperationsObligation): Observable<APIResponseWrapper<M.AdminOperationsObligation>> {
      return request.obligationId
        ? this.http.put<APIResponseWrapper<M.AdminOperationsObligation>>(`${this.adminOperationsUrl}/obligations/${request.obligationId}`, request)
        : this.http.post<APIResponseWrapper<M.AdminOperationsObligation>>(`${this.adminOperationsUrl}/obligations`, request);
    }

    launchAdminOperationsCycle(obligationId: number, dueDate?: string, notes?: string): Observable<APIResponseWrapper<M.AdminOperationsCycle>> {
      return this.http.post<APIResponseWrapper<M.AdminOperationsCycle>>(
        `${this.adminOperationsUrl}/obligations/${obligationId}/launch`, null, { params: this.params({ dueDate, notes }) });
    }

    adminOperationsCycles(scope: AdminOperationsScopeParams & { from?: string; to?: string; status?: string } = {}):
      Observable<APIResponseWrapper<M.AdminOperationsCycle[]>> {
      return this.http.get<APIResponseWrapper<M.AdminOperationsCycle[]>>(`${this.adminOperationsUrl}/cycles`, { params: this.params(scope) });
    }

    adminOperationsCycle(cycleId: number): Observable<APIResponseWrapper<M.AdminOperationsCycle>> {
      return this.http.get<APIResponseWrapper<M.AdminOperationsCycle>>(`${this.adminOperationsUrl}/cycles/${cycleId}`);
    }

    transitionAdminOperationsCycle(cycleId: number, targetStatus: string, notes?: string, reason?: string):
      Observable<APIResponseWrapper<M.AdminOperationsCycle>> {
      return this.http.post<APIResponseWrapper<M.AdminOperationsCycle>>(`${this.adminOperationsUrl}/cycles/${cycleId}/transition`,
        { targetStatus, notes, reason });
    }

    addAdminOperationsEvidence(cycleId: number, request: M.AdminOperationsEvidence):
      Observable<APIResponseWrapper<M.AdminOperationsEvidence>> {
      return this.http.post<APIResponseWrapper<M.AdminOperationsEvidence>>(`${this.adminOperationsUrl}/cycles/${cycleId}/evidence`, request);
    }

    adminOperationsCalendar(year: number, scope: AdminOperationsScopeParams = {}): Observable<APIResponseWrapper<M.AdminOperationsCalendar>> {
      return this.http.get<APIResponseWrapper<M.AdminOperationsCalendar>>(`${this.adminOperationsUrl}/calendar`,
        { params: this.params({ ...scope, year }) });
    }

    adminOperationsDocuments(scope: AdminOperationsScopeParams & { status?: string } = {}):
      Observable<APIResponseWrapper<M.AdminOperationsDocument[]>> {
      return this.http.get<APIResponseWrapper<M.AdminOperationsDocument[]>>(`${this.adminOperationsUrl}/documents`, { params: this.params(scope) });
    }

    saveAdminOperationsDocument(request: M.AdminOperationsDocument): Observable<APIResponseWrapper<M.AdminOperationsDocument>> {
      return request.documentId
        ? this.http.put<APIResponseWrapper<M.AdminOperationsDocument>>(`${this.adminOperationsUrl}/documents/${request.documentId}`, request)
        : this.http.post<APIResponseWrapper<M.AdminOperationsDocument>>(`${this.adminOperationsUrl}/documents`, request);
    }

    verifyAdminOperationsDocument(documentId: number, verificationStatus: string, reason?: string):
      Observable<APIResponseWrapper<M.AdminOperationsDocument>> {
      return this.http.post<APIResponseWrapper<M.AdminOperationsDocument>>(`${this.adminOperationsUrl}/documents/${documentId}/verify`,
        { verificationStatus, reason });
    }

    activateKsaAdminOperationsTemplate(owningCompanyId: number, year: number): Observable<APIResponseWrapper<Record<string, unknown>>> {
      return this.http.post<APIResponseWrapper<Record<string, unknown>>>(`${this.adminOperationsUrl}/templates/ksa/activate`,
        { owningCompanyId, year });
    }

    previewAdminOperationsImport(owningCompanyId: number, file: File): Observable<APIResponseWrapper<M.AdminOperationsImportPreview>> {
      const formData = new FormData();
      formData.append('file', file);
      return this.http.post<APIResponseWrapper<M.AdminOperationsImportPreview>>(`${this.adminOperationsUrl}/import/preview`,
        formData, { params: this.params({ owningCompanyId }) });
    }

    commitAdminOperationsImport(owningCompanyId: number, rows: M.AdminOperationsImportRow[]):
      Observable<APIResponseWrapper<Record<string, number>>> {
      return this.http.post<APIResponseWrapper<Record<string, number>>>(`${this.adminOperationsUrl}/import/commit`,
        { owningCompanyId, rows });
    }

    exportAdminOperations(year: number, scope: AdminOperationsScopeParams = {}): Observable<HttpResponse<Blob>> {
      return this.http.get(`${this.adminOperationsUrl}/export`, {
        params: this.params({ ...scope, year }), observe: 'response', responseType: 'blob'
      });
    }

    evaluateAdminOperations(owningCompanyId: number): Observable<APIResponseWrapper<Record<string, number>>> {
      return this.http.post<APIResponseWrapper<Record<string, number>>>(`${this.adminOperationsUrl}/evaluate`, null,
        { params: this.params({ owningCompanyId }) });
    }
  };
}
